import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  OnInit,
} from '@angular/core';
import { OpsSelectComponent } from '../ops-select/ops-select.component';
import { OpsInputComponent } from '../ops-input/ops-input.component';
import { LocalStorageService, User, UsersService } from '@ops/shared-types';
import { Subscription } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'ops-invite-member',
  standalone: true,
  imports: [
    CommonModule,
    OpsInputComponent,
    OpsSelectComponent,
    TranslateModule,
  ],
  templateUrl: './ops-invite-member.component.html',
  styleUrls: ['./ops-invite-member.component.scss'],
})
export class OpsInviteMemberComponent implements OnDestroy, OnInit {
  private _isPreview = true;

  @Output() invitedMember = new EventEmitter<User>();

  @Input() set isPreview(val: boolean) {
    this._isPreview = val;

    if (val) {
      this.errorsTrack = {};
      this.memberEmail = '';
      this.memberRole = null;
      this.memberRoleDisplay = null;
    }
  }
  get isPreview(): boolean {
    return this._isPreview;
  }

  memberEmail = '';
  memberRole = null;
  memberRoleDisplay = null; // For display in the select component
  errorsTrack = {};
  roleOptions: string[] = [];
  roleValues: string[] = ['admin', 'staff', 'guest'];

  constructor(
    private usersService: UsersService,
    private ls: LocalStorageService,
    private translateService: TranslateService
  ) {}

  subInviteMember: Subscription;

  ngOnInit(): void {
    this.loadTranslations();

    // Listen for language changes
    this.translateService.onLangChange.subscribe(() => {
      // console.log('Language changed, updating translations...');
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.subInviteMember?.unsubscribe) {
      this.subInviteMember.unsubscribe();
    }
  }

  loadTranslations() {
    this.translateService
      .get([
        'auth.inviteMember.admin',
        'auth.inviteMember.staff',
        'auth.inviteMember.guest',
      ])
      .subscribe((translations) => {
        this.roleOptions = [
          translations['auth.inviteMember.admin'],
          translations['auth.inviteMember.staff'],
          translations['auth.inviteMember.guest'],
        ];
        // console.log('Role options loaded:', this.roleOptions);
        
        // Update the display value if a role is already selected
        this.updateDisplayValueForCurrentLanguage();
      });
  }

  inviteMember() {
    if (this.memberEmail) {
      if (this.subInviteMember?.unsubscribe) {
        this.subInviteMember.unsubscribe();
      }

      // console.log('Inviting member with role:', this.memberRole);
      
      this.subInviteMember = this.usersService
        .createUser(this.memberEmail, {
          company: this.ls.getCurrentCompany()?._id,
          role: this.memberRole,
        })
        .subscribe(
          (userInvited: string) => {
            this.translateService
              .get('auth.inviteMember.invitationSent', { email: this.memberEmail })
              .subscribe((translation) => {
                window.alert(translation);
              });
            this.resetInvite();
            this.invitedMember.emit(JSON.parse(userInvited) as unknown as User);
          },
          (error) => {
            const errorToJson = JSON.parse(error?.error || '{}');
            if (errorToJson?.error?.additionalErrorInfo?.limitation) {
              this.translateService
                .get('auth.inviteMember.subscriptionLimitExceeded')
                .subscribe((translation) => {
                  window.alert(translation);
                });
            } else if (errorToJson?.error?.message) {
              const errorsList = errorToJson?.error?.message.split('.');

              this.translateService
                .get([
                  'auth.inviteMember.updateError',
                  'auth.inviteMember.someFieldsInvalid',
                  'auth.inviteMember.pleaseCheckFields',
                ])
                .subscribe((translations) => {
                  const errorDescription = `${translations['auth.inviteMember.updateError']}\n\n${translations['auth.inviteMember.someFieldsInvalid']}\n${translations['auth.inviteMember.pleaseCheckFields']}\n\n`;
                  const fullMessage = errorDescription + errorsList.join('.\n');
                  window.alert(fullMessage);
                });
            }
          }
        );
    } else {
      this.translateService
        .get('auth.inviteMember.pleaseEnterValidEmail')
        .subscribe((translation) => {
          alert(translation);
        });
    }
  }

  onRoleChange(value) {
    // Map the translated role back to the English value
    const roleIndex = this.roleOptions.indexOf(value);
    if (roleIndex !== -1) {
      this.memberRole = this.roleValues[roleIndex];
      this.memberRoleDisplay = value; // Keep the display value for the select component
      // console.log('Role selected:', value, '-> Mapped to:', this.memberRole);
    } else {
      this.memberRole = value; // Fallback in case of any issues
      this.memberRoleDisplay = value;
      // console.log('Role fallback:', value);
    }
  }

  private updateDisplayValueForCurrentLanguage() {
    if (this.memberRole && this.roleOptions.length > 0) {
      const roleIndex = this.roleValues.indexOf(this.memberRole);
      if (roleIndex !== -1 && roleIndex < this.roleOptions.length) {
        this.memberRoleDisplay = this.roleOptions[roleIndex];
        // console.log('Updated display value for current language:', this.memberRoleDisplay);
      } else {
        // If the role is not found or index is out of bounds, clear the display
        // console.log('Role not found in current language options, clearing display');
        this.memberRoleDisplay = null;
      }
    }
  }

  get isDisabled(): boolean {
    if (
      this.memberRoleDisplay &&
      this.memberEmail &&
      Object.keys(this.errorsTrack).length === 0
    ) {
      return false;
    }

    return true;
  }

  resetInvite() {
    this.memberEmail = '';
    this.memberRole = null;
    this.memberRoleDisplay = null;
    this.errorsTrack = {};
    // console.log('Form reset');
  }

  onErrorHandler(error: string, field?: string) {
    if (!error || error === '') {
      delete this.errorsTrack[field];
    } else {
      this.errorsTrack[field] = error;
    }
  }

  onInputChange(newValue: string): void {
    this.memberEmail = newValue;
  }
}
